/**
 * @fileoverview Custom hooks and components for scroll spy functionality.
 */

import React, {
  useEffect,
  useRef,
  createContext,
  useState,
  FunctionComponent,
  useContext,
  ReactNode,
  PropsWithChildren,
} from "react";
import {useIntersection} from "react-use";

/**
 * Props for scroll spy functionality.
 *
 * @param {string} id Unique identifier for the scroll spy element.
 * @param {Function} [onIntersection] Optional callback when element intersects.
 */
type ScrollSpyProps = {
  id: string;
  onIntersection?: (id: string) => void;
};

/**
 * Custom hook that provides scroll spy functionality for a specific element.
 * Tracks when an element is fully visible in the viewport and manages active state.
 *
 * @param {ScrollSpyProps} props The scroll spy props.
 * @param {string} props.id Unique identifier for the element.
 * @param {Function} [props.onIntersection] Optional callback when element intersects.
 * @returns {Object} Object containing the ref and viewport state.
 * @returns {RefObject} returns.scrollSpyRef Ref to attach to the element to spy on.
 * @returns {boolean} returns.wasInViewport Whether the element has been in the viewport.
 */
export const useScrollSpyRef = ({id, onIntersection}: ScrollSpyProps) => {
  const [wasInViewport, setWasInViewport] = useState(false);
  const {setActiveId} = useScrollSpyContext();
  const scrollSpyRef = useRef(null);

  const {intersectionRatio = 0} =
    useIntersection(scrollSpyRef, {
      root: document.querySelector(".js-reporting-scroll-container"),
      rootMargin: "0px",
      threshold: 1,
    }) ?? {};

  useEffect(() => {
    if (intersectionRatio === 1) {
      setWasInViewport(true);
      setActiveId(id);
      onIntersection?.(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, intersectionRatio]);

  return {scrollSpyRef, wasInViewport};
};

/**
 * Component that provides scroll spy functionality through a render prop pattern.
 * Uses the useScrollSpyRef hook internally and passes the result to children.
 *
 * @param {ScrollSpyProps & {children: Function}} props The component props.
 * @param {string} props.id Unique identifier for the element.
 * @param {Function} [props.onIntersection] Optional callback when element intersects.
 * @param {Function} props.children Render prop function that receives the hook result.
 * @returns {JSX.Element} The rendered children with hook result.
 */
export const UseScrollSpy = ({
  children,
  ...props
}: ScrollSpyProps & {
  children: (props: ReturnType<typeof useScrollSpyRef>) => ReactNode;
}) => <>{children(useScrollSpyRef(props))}</>;

/**
 * Context for managing scroll spy state across components.
 */
const scrollSpyContext = createContext({
  activeId: "",
  setActiveId: (id: string) => {},
});

/**
 * Provider component that manages scroll spy state for child components.
 * Tracks the currently active scroll spy element ID.
 *
 * @param {Object} props The provider props.
 * @param {ReactNode} props.children Child components that can use scroll spy context.
 * @returns {JSX.Element} The context provider with children.
 */
export const ScrollSpyProvider: FunctionComponent<PropsWithChildren<{}>> = ({
  children,
}) => {
  const [activeId, setActiveId] = useState("");

  return (
    <scrollSpyContext.Provider value={{activeId, setActiveId}}>
      {children}
    </scrollSpyContext.Provider>
  );
};

/**
 * Hook to access the scroll spy context.
 * Must be used within a ScrollSpyProvider.
 *
 * @returns {Object} The scroll spy context value.
 * @returns {string} returns.activeId The currently active scroll spy element ID.
 * @returns {Function} returns.setActiveId Function to set the active element ID.
 */
export const useScrollSpyContext = () => useContext(scrollSpyContext);
